













import { Component, Prop, mixins, namespace } from 'nuxt-property-decorator';
import UserMenuMobileMainMenuItem from '@/components/global/UserMenuMobile/UserMenuMobileMainMenu/UserMenuMobileMainMenuItem.vue';
import JobsHiringsCountMixin from '@/components/common/BravadoRightNavigation/JobsHiringsCountMixin';
import WarRoomUnreadPostsCountMixin from '@/components/common/BravadoRightNavigation/WarRoomUnreadPostsCountMixin';
import { DEFAULT_FEED_PATH } from '@/components/partials/war-room/constants';
import currentUserUnseenNotificationsQuery from '@/components/global/HeaderMenu/HeaderMenuNotifications/CurrentUserUnseenNotifications.query';
import { menuItemKeys, menuItems } from '@/components/global/UserMenuMobile/UserMenuMobileMainMenu/constants';
import { ACADEMY_NEW_MENU_TOOLTIP, PP_NEW_MENU_TOOLTIP } from '@/components/common/BravadoRightNavigation/constants';
import { JOBS_NEW_MENU_TOOLTIP, JOBS_CANDIDATE_PROFILE_PAGE } from '@/constants';
import { getPortfolioPath } from '@/utils/routes';
import type { IMenuItem, IMoreMenuItem } from '@/components/global/UserMenuMobile/UserMenuMobileMainMenu/constants';
import type { CurrentUser } from '@/types/graphql';

const WEB_PLATFORM = 'WEB';
const UIStore = namespace('ui');

@Component<UserMenuMobileMainMenu>({
  name: 'UserMenuMobileMainMenu',
  components: {
    UserMenuMobileMainMenuItem,
  },
  apollo: {
    currentUser: {
      query: currentUserUnseenNotificationsQuery,
      variables: {
        platform: WEB_PLATFORM,
      },
      skip() {
        return !this.$auth.loggedIn;
      },
      prefetch: false,
    },
  },
  watch: {
    isSubmenuOpened(newValue) {
      this.activeItem = newValue ? menuItemKeys.MORE : null;
    },
  },
})
export default class UserMenuMobileMainMenu extends mixins(JobsHiringsCountMixin, WarRoomUnreadPostsCountMixin) {
  @Prop({ default: false }) isSubmenuOpened!: boolean;

  @UIStore.State('isNotificationsDropdownOpen') isNotificationsDropdownOpen!: boolean;
  @UIStore.Mutation('toggleNotificationsDropdown') toggleNotificationsDropdown!: (isOpen: boolean) => void;

  protected currentUser: CurrentUser | null = null;
  protected activeItem: string | null = null;

  get submenuIcon(): string {
    const moreItem = menuItems[menuItemKeys.MORE] as IMoreMenuItem;

    return this.isSubmenuOpened ? moreItem.iconOpened : moreItem.icon;
  }

  get submenuName(): string {
    const moreItem = menuItems[menuItemKeys.MORE] as IMoreMenuItem;

    return this.isSubmenuOpened ? moreItem.nameOpened : moreItem.name;
  }

  get hasUnseenNotifications(): boolean {
    return !!this.currentUser?.unseenWarRoomNotifications;
  }

  get hasUnreadNotifications(): boolean {
    return !!this.currentUser?.unreadWarRoomNotifications;
  }

  get hasUnreadConnections(): boolean {
    return !!this.currentUser?.unreadConnectionRequestsCount;
  }

  get notificationsIcon(): string {
    const notificationsItem = menuItems[menuItemKeys.NOTIFICATIONS] as IMenuItem & { iconUnread: string };

    return this.$auth.loggedIn &&
      (this.hasUnseenNotifications || this.hasUnreadNotifications || this.hasUnreadConnections)
      ? notificationsItem.iconUnread
      : notificationsItem.icon;
  }

  get jobsIcon(): string {
    const jobsItem = menuItems[menuItemKeys.JOBS];

    if (this.$tooltip.isPresent(JOBS_NEW_MENU_TOOLTIP) && this.hiringCount) return jobsItem.iconUnread as string;

    return jobsItem.icon;
  }

  get menu(): Array<IMenuItem> {
    const feedMenuItem = this.$auth.loggedIn ? menuItemKeys.FEED_LOGEED : menuItemKeys.FEED;

    const portfolioPath = this.$auth.loggedIn ? getPortfolioPath(this.$auth.user.slug as string) : '';

    const items = [
      {
        ...menuItems[feedMenuItem],
        to: { path: DEFAULT_FEED_PATH },
        visible: true,
        route: [this.$pages.POSTS_PAGE.path, '/war-room/categories', '/war-room/topics'],
        action: () => this.setActive(menuItemKeys.FEED),
        ...(this.$auth.loggedIn && this.unreadPostsMessage && { unreadCountLabel: this.unreadPostsMessage }),
      },
      {
        ...menuItems[menuItemKeys.SELLER_PORTFOLIO],
        to: portfolioPath,
        visible: this.$auth.loggedIn,
        route: [portfolioPath],
        action: () => this.setActive(menuItemKeys.SELLER_PORTFOLIO),
        tooltip: PP_NEW_MENU_TOOLTIP,
      },
      {
        ...menuItems[menuItemKeys.ACADEMY],
        to: { name: 'academy' },
        visible: !this.$auth.loggedIn,
        route: ['/academy'],
        action: () => this.setActive(menuItemKeys.ACADEMY),
        tooltip: ACADEMY_NEW_MENU_TOOLTIP,
      },
      {
        ...menuItems[menuItemKeys.JOBS],
        to: this.$auth.loggedIn ? JOBS_CANDIDATE_PROFILE_PAGE : '/jobs/welcome',
        icon: this.jobsIcon,
        visible: true,
        route: [JOBS_CANDIDATE_PROFILE_PAGE.path],
        action: () => this.setActive(menuItemKeys.JOBS),
        tooltip: JOBS_NEW_MENU_TOOLTIP,
      },
      {
        ...menuItems[menuItemKeys.SALARIES],
        visible: !this.$auth.loggedIn,
        isLink: true,
        href: '/salaries/',
        action: () => this.setActive(menuItemKeys.SALARIES),
      },
      {
        ...menuItems[menuItemKeys.NOTIFICATIONS],
        icon: this.notificationsIcon,
        visible: this.$auth.loggedIn && !this.$device.isTablet,
        action: this.toggleNotifications,
      },
      {
        name: this.submenuName,
        icon: this.submenuIcon,
        key: menuItemKeys.MORE,
        visible: this.$auth.loggedIn,
        action: () => {
          if (!this.isSubmenuOpened) {
            this.$emit('on-open-submenu');
          }
        },
      },
      {
        ...menuItems[menuItemKeys.LOG_IN],
        to: { query: { ...this.$route.query, ...{ dynamic_modal: this.$constants.dynamicModals.SIGN_IN_MODAL } } },
        visible: !this.$auth.loggedIn,
      },
    ];
    return items.map(item => ({ ...item, isActive: this.isActive(item.key) })) as Array<IMenuItem | IMoreMenuItem>;
  }

  toggleNotifications() {
    if (!this.isNotificationsDropdownOpen) {
      this.setActive(menuItemKeys.NOTIFICATIONS);
    } else {
      this.setActive(null);
    }

    this.toggleNotificationsDropdown(!this.isNotificationsDropdownOpen);
  }

  isNew(item: IMenuItem | IMoreMenuItem) {
    return !!(this.$auth.loggedIn && item.tooltip && !this.$tooltip.isPresent(item.tooltip));
  }

  handleClickItem(item: IMenuItem | IMoreMenuItem) {
    item.action && item.action();

    if (this.isNew(item)) {
      this.$tooltip.close(item.tooltip!);
    }
  }

  setActive(key: string | null): void {
    this.activeItem = key;
  }

  isActive(itemKey: string): boolean {
    return itemKey === this.activeItem;
  }
}
